/**
 * useSpanishTrackingNumber — single shared fetch+cache for the dedicated
 * CallRail tracking number assigned to /es/* pages.
 *
 * - Fetches /api/callrail/trackers/spanish ONCE per session (not per render)
 * - Falls back gracefully to the main EN number if not yet provisioned
 * - Returns formatted display + tel: link so components don't re-format
 *
 * Usage:
 *   const { displayNumber, telLink, configured } = useSpanishTrackingNumber();
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_DISPLAY = '(817) 985-3337';
const FALLBACK_TEL = 'tel:8179853337';

// Module-level cache so multiple components mounted on the same page share
// a single network request (Home + Service Template + Contact all need this).
let cachedPromise = null;
let cachedResult = null;

const fetchSpanishTracker = () => {
  if (cachedResult) return Promise.resolve(cachedResult);
  if (cachedPromise) return cachedPromise;
  cachedPromise = axios
    .get(`${BACKEND_URL}/api/callrail/trackers/spanish`)
    .then((res) => {
      cachedResult = res.data;
      return cachedResult;
    })
    .catch(() => {
      cachedResult = { configured: false };
      return cachedResult;
    });
  return cachedPromise;
};

const useSpanishTrackingNumber = () => {
  const [state, setState] = useState({
    configured: false,
    displayNumber: FALLBACK_DISPLAY,
    telLink: FALLBACK_TEL,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    fetchSpanishTracker().then((data) => {
      if (cancelled) return;
      if (data?.configured && data?.tracking_number_display) {
        setState({
          configured: true,
          displayNumber: data.tracking_number_display,
          telLink: data.tel_link || FALLBACK_TEL,
          loading: false,
        });
      } else {
        setState({
          configured: false,
          displayNumber: FALLBACK_DISPLAY,
          telLink: FALLBACK_TEL,
          loading: false,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

export default useSpanishTrackingNumber;
